import React, { useState, useEffect } from 'react';
import { Play, Video as VideoIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Video = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
      if (response.data.length > 0) {
        setSelectedVideo(response.data[0]);
      }
    } catch (error) {
      console.error('Erreur chargement vidéos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    // Convertir l'URL YouTube en URL embed
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  if (loading) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <p>Chargement...</p>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        <VideoIcon size={64} style={{ color: '#C9A961', marginBottom: '24px' }} />
        <h1 style={{ fontSize: '32px', marginBottom: '16px' }}>Visite Guidée</h1>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', textAlign: 'center' }}>
          Découvrez bientôt Nina Care Institut en vidéo ! 
          Une visite virtuelle de notre institut vous attend.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '80px 20px 40px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '60px' }}>
        <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>
          Visite Guidée
        </h1>
        <p style={{ fontSize: '20px', color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
          Découvrez Nina Care Institut comme si vous y étiez
        </p>
      </div>

      {/* Vidéo principale */}
      {selectedVideo && (
        <div style={{ marginBottom: '60px' }}>
          <div style={{ 
            position: 'relative', 
            paddingBottom: '56.25%', 
            height: 0, 
            overflow: 'hidden',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <iframe
              src={getYouTubeEmbedUrl(selectedVideo.url)}
              title={selectedVideo.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          </div>
          
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ fontSize: '28px', marginBottom: '12px' }}>
              {selectedVideo.title}
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              {selectedVideo.description}
            </p>
          </div>
        </div>
      )}

      {/* Liste des autres vidéos */}
      {videos.length > 1 && (
        <div>
          <h3 style={{ fontSize: '24px', marginBottom: '24px' }}>Autres vidéos</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
            gap: '24px' 
          }}>
            {videos.filter(v => v.id !== selectedVideo?.id).map(video => (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-light)',
                  transition: 'transform 0.2s',
                  background: 'white'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ 
                  position: 'relative', 
                  paddingBottom: '56.25%',
                  background: '#f3f4f6'
                }}>
                  <img
                    src={video.thumbnail || `https://img.youtube.com/vi/${video.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}/maxresdefault.jpg`}
                    alt={video.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'rgba(201, 169, 97, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Play size={24} style={{ color: 'white', marginLeft: '4px' }} />
                  </div>
                </div>
                <div style={{ padding: '16px' }}>
                  <h4 style={{ fontSize: '16px', marginBottom: '8px' }}>
                    {video.title}
                  </h4>
                  <p style={{ 
                    fontSize: '14px', 
                    color: 'var(--text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {video.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Video;
